import { useState } from "react"
import { IconLinkOutline18 as LinkIcon } from "nucleo-ui-outline-18"

import { Button, Dialog, IconButton, Input } from "@productlane/ui"
import {
	DropdownMenu,
	DropdownMenuGroup,
	DropdownMenuItem,
} from "@productlane/ui/src/components/internal/dropdown"
import { EllipsisIcon as MoreHorizontal } from "@productlane/ui/src/icons/project/ellipsis-icon"

import { toast } from "@/components/toast"
import { api } from "@/lib/api"

interface Props {
	workspaceId: string
	domain: string
}

export function WorkspaceOptionsMenu({ workspaceId, domain }: Props) {
	const utils = api.useUtils()
	const [editOpen, setEditOpen] = useState(false)
	const [domainInput, setDomainInput] = useState(domain)

	const updateDomain = api.analytics.updateWorkspaceDomain.useMutation({
		onSuccess: async () => {
			toast.success({ title: "Workspace URL updated", icon: LinkIcon })
			setEditOpen(false)
			await utils.analytics.listWorkspaces.invalidate()
		},
		onError: (err) => {
			toast.error({ title: "Failed to update URL", description: err.message })
		},
	})

	return (
		<>
			<DropdownMenu
				trigger={<IconButton Icon={MoreHorizontal} variant="ghost" />}
				contentProps={{ side: "bottom", align: "end" }}
			>
				<DropdownMenuGroup>
					<DropdownMenuItem
						Icon={LinkIcon}
						onClick={() => {
							setDomainInput(domain)
							setEditOpen(true)
						}}
					>
						Edit workspace URL
					</DropdownMenuItem>
				</DropdownMenuGroup>
			</DropdownMenu>

			<Dialog
				isOpen={editOpen}
				onOpenChange={setEditOpen}
				title="Edit workspace URL"
				description="Change the subdomain this workspace is reachable at."
				footer={
					<>
						<Button
							variant="outline"
							onClick={() => {
								setEditOpen(false)
							}}
						>
							Cancel
						</Button>
						<Button
							disabled={
								updateDomain.isLoading ||
								domainInput.trim().length === 0 ||
								domainInput.trim() === domain
							}
							onClick={() => {
								updateDomain.mutate({
									workspaceId,
									domain: domainInput.trim(),
								})
							}}
						>
							Save
						</Button>
					</>
				}
			>
				<div className="space-y-2 px-1 py-2">
					<label
						className="text-xs text-tertiary"
						htmlFor="workspace-domain-input"
					>
						Subdomain
					</label>
					<div className="flex items-center gap-1.5">
						<Input
							id="workspace-domain-input"
							name="workspace-domain-input"
							value={domainInput}
							autoFocus
							onChange={(e) => {
								setDomainInput(e.target.value)
							}}
						/>
						<span className="whitespace-nowrap text-sm text-tertiary">
							.productlane.com
						</span>
					</div>
				</div>
			</Dialog>
		</>
	)
}
